// src/utils/translateX.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useContext } from 'react';
import i18n from 'i18next';
import { languageContextReload } from 'lang/languageContextReload';

// *******************************************************************************
// MAIN
// *******************************************************************************
const translateX = (id, replaces = {}) => {
  // If a ReactNode or any non-string was passed, do NOT break it.
  if (typeof id !== 'string') return id;

  let t = i18n.t(id);

  // If no replaces provided, return translation as-is
  if (!replaces || typeof replaces !== 'object' || Array.isArray(replaces)) {
    return t;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Object map -> supports %TOKEN% and {{token}} placeholders
  // Accepts primitives or React elements in values.
  // Keys may be provided as NAME, %NAME%, {{name}}, name, etc.
  // If any value is a React element, return an array of nodes.
  // ─────────────────────────────────────────────────────────────────────────────
  const normMap = normalizeTokenMap(replaces);
  const hasReact = Object.values(normMap).some(v => React.isValidElement(v));

  if (!hasReact) {
    // Fast path: only primitive replacements -> return string
    Object.keys(normMap).forEach(keyUpper => {
      const val = normMap[keyUpper];
      // Support both %TOKEN% and {{token}} formats
      const percentRegex = new RegExp(`%${escapeRegExp(keyUpper)}%`, 'gi');
      const bracesRegex = new RegExp(`\\{\\{\\s*${escapeRegExp(keyUpper)}\\s*\\}\\}`, 'gi');
      t = t.replace(percentRegex, String(val));
      t = t.replace(bracesRegex, String(val));
    });
    return t;
  }

  // Rich path: build an array mixing strings and React elements
  // Regex matches both %TOKEN% and {{token}} formats
  const out = [];
  const re = /%([A-Z0-9_]+)%|\{\{\s*([A-Z0-9_]+)\s*\}\}/gi;
  let last = 0;
  let idx = 0;
  let m;

  while ((m = re.exec(t)) !== null) {
    if (m.index > last) out.push(t.slice(last, m.index));
    // m[1] is from %TOKEN%, m[2] is from {{token}}
    const tokenUpper = String(m[1] || m[2] || '').toUpperCase();
    const value = normMap[tokenUpper];

    if (React.isValidElement(value)) {
      out.push(React.cloneElement(value, { key: `tx-${idx++}` }));
    } else if (value != null) {
      out.push(String(value));
    } else {
      out.push(m[0]); // preserve unresolved token
    }
    last = re.lastIndex;
  }
  if (last < t.length) out.push(t.slice(last));

  return out;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default translateX;

// *******************************************************************************
// HOOK - USE LANGUAGE UPDATE
// *******************************************************************************
// Call this hook in any component that uses translateX() to ensure it re-renders when language changes
// Example: const MyComponent = () => { useLanguageUpdate(); return <div>{translateX('key')}</div>; };
export const useLanguageUpdate = () => {
  useContext(languageContextReload); // Subscribe to language version changes
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripDelimiters(s) {
  const trimmed = String(s).trim();
  // Strip %...% format
  const percentMatch = trimmed.match(/^%?(.*?[^%])%?$/);
  // Strip {{...}} format
  const bracesMatch = trimmed.match(/^\{\{\s*(.*?)\s*\}\}$/);

  if (bracesMatch) return bracesMatch[1];
  if (percentMatch) return percentMatch[1];
  return trimmed;
}

function normalizeTokenCore(raw) {
  const core = stripDelimiters(raw);
  if (!core) return '';
  // Token-like: letters/numbers/underscore only
  if (!/^[A-Za-z0-9_]+$/.test(core)) return '';
  return core.toUpperCase();
}

function normalizeTokenMap(map) {
  // Normalize keys to UPPERCASE without surrounding %
  const out = {};
  Object.keys(map).forEach(rawKey => {
    const core = normalizeTokenCore(rawKey);
    if (!core) return;
    out[core] = map[rawKey];
  });
  return out;
}
